import { useEffect, useMemo, useState } from 'react';
import { CampaignStepState, CampaignStepsStatesMapper, CampaignStep } from '../types';
import {
  getHasQuietHours,
  getHasPriorityLevel,
  getHasCampaignRateLimit,
  getHasConversions,
  getHasExperiments,
  getHasList,
  getHasDisplayPriority,
} from '../components/utils';
import { DEFAULT_CAMPAIGN_EDITOR_STEP_STATES_MAPPER, useCampaignContext } from '../context';
import { getHasSendTimeOptimization } from '../utils';

export const getCurrentEditableCampaignStep = (campaignSteps: CampaignStepsStatesMapper) =>
  Object.entries(campaignSteps).find(([, vals]) => vals.isEditable) as
    | [CampaignStep, CampaignStepState]
    | undefined;

export const useCampaignStepsState = () => {
  const [campaignSteps, setCampaignSteps] = useState(DEFAULT_CAMPAIGN_EDITOR_STEP_STATES_MAPPER);
  const [currentEditableStep] = useMemo(
    () => getCurrentEditableCampaignStep(campaignSteps) || [],
    [campaignSteps],
  );

  const { campaign } = useCampaignContext();

  const editCampaignStep = (campaignStep: CampaignStep) => {
    setCampaignSteps((state) => {
      const campaignStepState = Object.keys(state).reduce(
        (acc, key) => ({
          ...acc,
          [key]: {
            ...state[key as CampaignStep],
            isOpen: key === campaignStep ? true : state[key as CampaignStep]?.isOpen,
            isEditable: key === campaignStep,
          },
        }),
        {} as CampaignStepsStatesMapper,
      );
      return campaignStepState;
    });
  };

  const completeCampaignStep = (campaignStep: CampaignStep, isComplete: boolean) => {
    setCampaignSteps((state) => ({
      ...state,
      [campaignStep]: { ...state[campaignStep], isComplete },
    }));
  };

  const closeCampaignStep = (campaignStep: CampaignStep) => {
    setCampaignSteps((state) => ({
      ...state,
      [campaignStep]: { ...state[campaignStep], isOpen: false, isEditable: false },
    }));
  };

  const viewCampaignStep = (campaignStep: CampaignStep) => {
    setCampaignSteps((state) => ({
      ...state,
      [campaignStep]: { ...state[campaignStep], isOpen: true, isEditable: false },
    }));
  };

  useEffect(() => {
    const deliveryComplete =
      getHasQuietHours(campaign?.customQuietHours) ||
      getHasPriorityLevel(campaign?.priorityLevel) ||
      getHasSendTimeOptimization(campaign) ||
      getHasCampaignRateLimit(campaign?.customRateLimit);
    const conversionsAndExperimentComplete =
      getHasConversions(campaign?.conversions) || getHasExperiments(campaign?.experiments);

    const audienceComplete = getHasList(campaign?.lists);
    const displayComplete = getHasDisplayPriority(campaign?.embeddedPriorityOrder);

    completeCampaignStep(CampaignStep.Audience, audienceComplete);
    completeCampaignStep(CampaignStep.AudienceEligibility, audienceComplete);
    completeCampaignStep(CampaignStep.ConversionAndExperiment, conversionsAndExperimentComplete);
    completeCampaignStep(CampaignStep.Delivery, deliveryComplete);
    completeCampaignStep(CampaignStep.Display, displayComplete);
  }, [campaign]);

  return {
    campaignSteps,
    viewCampaignStep,
    closeCampaignStep,
    completeCampaignStep,
    editCampaignStep,
    currentEditableStep,
  };
};
